import React, { FC, useState } from "react";
import { Helmet } from "react-helmet";
import SocialsList from "shared/SocialsList/SocialsList";
import { contactApi } from "services/apiServices";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./../../styles/style.css"

export interface PageContactProps {
  className?: string;
}

const PageContact: FC<PageContactProps> = ({ className = "" }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async () => {
    var data = {
      email: email,
      message: message,
      name: name,
      subject: "Contact Us - Europe Tours & Travels",
      templateName: "NEW-EMAIL"
    };
    const response = await contactApi(data);
    if (response.data) {
      if (response.data.response === "success") {
        toast(response.data.response);
        setTimeout(() => {
          window.location.reload();
        }, 1000)
      } else {
        toast.error(response.data.message, {
          position: toast.POSITION.TOP_CENTER
        });
      }
    }
  }

  return (
    <div className={`py-12 md:py-20 bg-gradient-to-b from-slate-50 to-white ${className}`}>
      <ToastContainer />
      <Helmet>
        <title>CONTACT | EUROPE TOURS & TRAVELS</title>
      </Helmet>
      
      <div className="mx-auto max-w-6xl px-4">
        {/* Header Section */}
        <div className="mb-12 text-center">
          <div className="mb-3 inline-block rounded-full bg-amber-100 px-4 py-2">
            <span className="text-sm font-semibold text-amber-700">GET IN TOUCH</span>
          </div>
          <h1 className="mb-3 text-4xl font-bold text-slate-900">Contact Us</h1>
          <p className="mx-auto max-w-2xl text-lg text-slate-600">
            Have questions? We'd love to hear from you. Get in touch with our team today.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:gap-12">
          {/* Contact Information Section */}
          <div className="space-y-6">

            {/* Swiss Office Card */}
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md">
              <div className="mb-4 flex items-start gap-3">
                <span className="text-2xl">🗺️</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">Swiss Office</h3>
                  <p className="text-sm text-slate-600">Europe Tours & Travels, Richtistrasse 7 Zurich 8304 Switzerland</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xl">☎️</span>
                <a href="tel:+41798847550" className="text-sm font-medium text-amber-600 hover:text-amber-700">
                +41 79 884 75 50
                </a>
              </div>
            </div>

            {/* Italy Office Card */}
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md">
              <div className="mb-4 flex items-start gap-3">
                <span className="text-2xl">🗺️</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">Italy Office</h3>
                  <p className="text-sm text-slate-600">Europe Tours & Travels, Via Abruzzi 94 Milan 20131 Italy</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xl">☎️</span>
                <a href="tel:+393516093653" className="text-sm font-medium text-amber-600 hover:text-amber-700">
                  +39 351 609 3653
                </a>
              </div>
            </div>

            

            {/* Email & Socials Card */}
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <span className="text-xl">💌</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">Email</h3>
                  <a href="mailto:info@ettravels.com" className="text-sm text-amber-600 hover:text-amber-700">
                    info@ettravels.com
                  </a>
                </div>
              </div>
              <div>
                <h3 className="mb-3 font-semibold text-slate-900">Follow Us</h3>
                <SocialsList className="" />
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="mb-6 text-2xl font-bold text-slate-900">Send us a Message</h2>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSubmit()
            }} className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Your Name
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 placeholder-slate-400 transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                  type="text"
                  placeholder="Enter your full name"
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                  required={true}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Your Email
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 placeholder-slate-400 transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                  type="email"
                  placeholder="Enter your email address"
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  required={true}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Message
                </label>
                <textarea
                  className="min-h-32 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 placeholder-slate-400 transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                  placeholder="Type your message here..."
                  onChange={(e) => {
                    setMessage(e.target.value);
                  }}
                  required={true}
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full rounded-lg bg-amber-600 px-6 py-3 font-semibold text-white transition-all hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
              >
                Send Message
              </button>
            </form>

            <p className="mt-4 text-center text-xs text-slate-500">
              We'll get back to you as soon as possible
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageContact;
